import React, { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { get, flatten, isEmpty, values } from 'lodash';
import PropTypes from 'prop-types';
import AppDatepicker from 'rc-calendar';
import AppDateRangePicker from 'rc-calendar/lib/RangeCalendar';
import es_ES from 'rc-calendar/lib/locale/es_ES';
import moment from 'moment';
import { connect } from 'formik';
import { ClickOutside } from '../../utils';
import { FlexContainer } from '../../layouts/Wrapper/Base.components';
import { Text, If } from '../../common';
import './Datepicker.overrides.less';
import { GRAY_DARK } from '../../../utils/colors';

const locale = { ...es_ES, monthFormat: 'MMMM' };

const InputContainer = styled(FlexContainer)`
    ${({ disabled }) => disabled && css`
        opacity: 0.5;
        pointer-events: none;
    `}
  ${({ width = '99.9%' }) => css`
      width: ${width};
      max-width: ${width === '99.9%' ? '290px' : width};
    `
}
  ${({ layout = 'column' }) => {
        if (layout === 'row') return css`margin: 0px 10px;`;
        return css`margin: 10px 0px;`;
    }}

`;
const Label = styled.label`
  color: #333333;
  margin-right: 10px;
  font-size: 12px;
  line-height: 1.75;
  font-weight: 600;

  ${props => props.error && `
    color: red;
  `}
`;

const Help = styled.span`
    color: black;
    font-size: 0.8em;
    padding: 10px 0px;
    ${props => props.error && `
      color: red;
    `}

    ${props => props.success && `
      border: solid 1px #4cb358;
    `}
`;

const DatePickerContainer = styled.div`
  position: relative;
  border-radius: 4px;
  background-color: #ffffff;
  outline: none;
  border: solid 1px ${GRAY_DARK};
  width: 100%;
  height: 34px;
  padding: 5px;
  display: flex;
  align-items: center;

  &:hover {
    border: 1px solid #4cb358;
  }
  ${({ success }) => {
        const isSuccess = success;
        const successColor = '#4cb358';
        return isSuccess && `
        border: solid 1px ${successColor};

        &:focus {
          box-shadow: 0 0 2pt 1pt ${successColor};
        }

        &:blur {
          box-shadow: 0 0 2pt 1pt ${successColor};
        }
    `;
    }}
  ${({ error }) => {
        const isError = error;
        const errorColor = '#db5959';
        return isError && `
      color: red;
      border: solid 1px ${errorColor};

      &:focus {
        box-shadow: 0 0 2pt 1pt ${errorColor};
      }

      &:blur {
        box-shadow: 0 0 2pt 1pt ${errorColor};
      }
    `;
    }}
`;

const FormDatePicker = ({
    range,
    layout,
    style,
    width,
    direction,
    name,
    type,
    error,
    success,
    label,
    onChange,
    defaultValue,
    hideValue,
    portalStyle,
    disabled,
    limitDays,
    mobile,
    valueFromField,
    onSelect: onSelectCallback,
    position,
    ...props
}) => {
    const initialDate = !isEmpty(valueFromField)
        ? values(valueFromField)
        : flatten([defaultValue || moment(), moment()]);

    const [show, toggleShow] = useState(false);
    const [value, setValue] = useState(initialDate);
    const containerRef = React.useRef(null);
    const isRangePicker = range;
    const toggle = () => toggleShow(!show);

    useEffect(() => {
        onChange(value);
    }, [value]);

    const onChangeDateRange = ([start = moment(), end = moment()]) => {
        setValue([start, end]);
    };

    const onChangeDate = (value) => {
        setValue([value, moment()]);
    };

    const onSelect = (date) => {
        onChangeDate(date);
        toggle();
    };

    const onClick = (e) => {
        e.stopPropagation();
        !show && toggle();
    };

    const onClickOutside = () => {
        if (show && !isRangePicker) setValue([moment(), moment()]);
        if (show) toggle();
    };

    const datePickerValue = () => {
        const [start, end] = value;

        if (isRangePicker) return `${start.format('DD/MM/YYYY')} - ${end.format('DD/MM/YYYY')}`;
        if (!isRangePicker && !show) return start.format('DD/MM/YYYY');
        if (!isEmpty(valueFromField)) return start.format('DD/MM/YYYY');
        return defaultValue.format('DD/MM/YYYY');
    };

    const _renderCalendar = () => {
        const [start] = value;
        return (
            <AppDatepicker
                {...props}
                value={start}
                locale={locale}
                onSelect={onSelect}
                onChange={onChangeDate}
                selectedValue={start}
                className='bo-datepicker'
                format='DD/MM/YYYY'
                showToday={false}
                onOk={toggle}
            />
        );
    };

    const _renderRangeCalendar = () => {
        const { disabledDate } = props;
        return (
            <AppDateRangePicker
                disabled
                locale={locale}
                showToday={false}
                dateInputPlaceholder={['Inicio', 'Fin']}
                defaultValue={value}
                selectedValue={value}
                onPanelChange={onChangeDateRange}
                onSelect={(date) => onChangeDateRange(date)}
                onChange={date => onSelectCallback(date)}
                onOk={toggle}
                showOk
                className='bo-datepicker'
                seperator='-'
                disabledYear
                format='DD/MM/YYYY'
                disabledDate={current => {
                    // This condition lets set custom disabled dates
                    if (disabledDate) return disabledDate(current);
                    // By default deisabled past dates, defined by limitDays property or 180 days by default
                    const date = moment().subtract(limitDays || 180, 'days');
                    return current.valueOf() < date.valueOf();
                }}
                style={mobile ? { width: 'min-content' } : {}}
                showDateInput={false}
      />
        );
    };

    const getPortalWrapperStyles = () => {
        const getPortalStylesByPosition = () => {
            if (mobile) {
                return {
                    top: '5px'
                };
            };
            switch (position) {
                case 'absolute':
                    return {
                        top: get(containerRef, 'current.offsetHeight', 0) + 5,
                        left: -1
                    };
                case 'fixed':
                    return {
                        top: get(containerRef, 'current.offsetTop', 0) +
                            get(containerRef, 'current.offsetHeight', 0) +
                            get(containerRef, 'current.parentElement.childNodes[1].offsetHeight', 0)
                    };
                default:
                    return {};
            }
        };
        return show && containerRef ? {
            position,
            zIndex: 9999,
            ...getPortalStylesByPosition(),
            ...portalStyle
        } : {};
        // Backup snippet
        /* return show && containerRef ? portalStyle || {
            position: 'fixed',
            top: mobile ? '5px' : get(containerRef, 'current.offsetTop', 0) +
        get(containerRef, 'current.offsetHeight', 0) +
        get(containerRef, 'current.parentElement.childNodes[1].offsetHeight', 0),
            zIndex: 9999
        } : {}; */
    };

    return (
        <InputContainer width={width} direction='column' disabled={disabled} style={style} layout={layout}>
            <FlexContainer direction={direction}>
                <Label type={type} error={error} success={success}>{label}</Label>
                <DatePickerContainer onClick={onClick} ref={containerRef}>
                    <Text color={hideValue ? '#ffffff' : '#333333'} text={datePickerValue()} />
                    <div style={{ marginLeft: 'auto' }}>
                        <span className='icon-ic-calendar' style={{ color: '#4cb358' }} />
                    </div>
                    <If condition={show} then={(
                        <ClickOutside onClickOutside={onClickOutside} style={getPortalWrapperStyles()}>
                            <If
                                condition={range}
                                then={(_renderRangeCalendar())}
                                else={(_renderCalendar())}
              />
                        </ClickOutside>
          )} />
                </DatePickerContainer>
            </FlexContainer>
            {error && <Help error={error}>{error} </Help>}
        </InputContainer>
    );
};

FormDatePicker.propTypes = {
    label: PropTypes.string,
    error: PropTypes.string,
    direction: PropTypes.string,
    success: PropTypes.bool,
    name: PropTypes.string,
    type: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.any,
    disabled: PropTypes.bool,
    onSelect: PropTypes.func,
    disabledDate: PropTypes.func,
    position: PropTypes.string
};

FormDatePicker.defaultProps = {
    direction: 'column',
    range: false,
    onChange: (value) => console.log('default change', value),
    disabled: false,
    onSelect: () => {},
    disabledDate: () => {},
    position: 'absolute'
};

const FormikDatePicker = connect(({
    field: { onChange, onBlur, name, value, ...field },
    form: { errors, dirty, touched, ...form },
    getDefaultSelected,
    formik: { setFieldValue },
    onChangeCallback,
    hideValue,
    ...props
}) => {
    return (
        <FormDatePicker
            name={name}
            hideValue={hideValue}
            {...field}
            valueFromField={value}
            error={(touched[name]) && errors[name]}
            {...props}
            onChange={([init, end]) => {
                onChangeCallback();
                setFieldValue(name, { init, end });
            }} />
    );
});

FormikDatePicker.defaultProps = {
    getDefaultSelected: (field) => (get(field, 'value')),
    onChangeCallback: () => {},
    hideValue: false
};

export { FormDatePicker, FormikDatePicker };
